import { checkDate } from "common/helpers/Date";
import { DateStringNumber } from "common/records";
import { defaultsFromJSON, DefaultsRecord, IDefaultsData } from "common/records/Defaults";
import {
  estimateDiscountFromJSON,
  EstimateDiscountRecord,
  IEstimateDiscountData,
} from "common/records/EstimateDiscount";
import { IEstimateFinanceOptionData } from "common/records/EstimateFinanceOption";
import { IEstimateGroupData } from "common/records/EstimateGroup";
import {
  estimatePaymentTermsFromJSON,
  EstimatePaymentTermsRecord,
  IEstimatePaymentTermsData,
} from "common/records/EstimatePaymentTerms";
import { EstimateKind } from "common/reducers/estimateEditor.actions";
import { Nullable } from "common/types";
import { fromJS, List, Map, Record } from "immutable";
import { round } from "lodash";

/**
 * Org commission-calculator methods (`estimator.commission_calculator.method` org setting). Single
 * source of truth for both the Commission Calculator modal and the org commission-config accordion.
 * String values are byte-identical to the saved org settings. Unlike rsf-app's enum, occ-app includes
 * `off` so it can drive the org accordion's option list (`off` = the calculator is hidden).
 */
export enum CommissionMethod {
  off = "off",
  marginWithOverhead = "margin_with_overhead",
  formula = "formula",
  greenline = "greenline",
  custom = "custom",
}

export const estimateFromJSON = (json: Partial<IEstimateData>): EstimateRecord => {
  const record: IEstimateRecord = { ...(json as any) };

  if (json.estimated_start && typeof json.estimated_start === "string") {
    const timezoneDate = new Date(json.estimated_start);
    record.estimated_start = new Date(
      `${json.estimated_start}T00:00:00.000${getTimezoneString(timezoneDate.getTimezoneOffset())}`,
    );
  }
  if (json.estimated_end && typeof json.estimated_end === "string") {
    const timezoneDate = new Date(json.estimated_end);
    record.estimated_end = new Date(
      `${json.estimated_end}T00:00:00.000${getTimezoneString(timezoneDate.getTimezoneOffset())}`,
    );
  }

  if (json.created_at) {
    record.created_at = checkDate(json.created_at);
  }

  if (json.updated_at) {
    record.updated_at = checkDate(json.updated_at);
  }

  if (json.payment_terms && !Record.isRecord(json.payment_terms)) {
    record.payment_terms = estimatePaymentTermsFromJSON(json.payment_terms);
  }

  if (json.groups) {
    record.group_ids = List(json.groups.map((g) => g.id));
  }

  if (json.discounts) {
    record.discounts = List(json.discounts.map((d) => estimateDiscountFromJSON(d)));
  }

  if (json.finance_options) {
    record.finance_option_ids = List(json.finance_options.map((o) => o.id));
  }

  if (json.defaults) {
    record.defaults = defaultsFromJSON(json.defaults);
  }

  if (json.metadata) {
    record.metadata = fromJS(json.metadata);
  }

  if (json.discountable_total) {
    record.running_discountable = json.discountable_total;
  }

  return new EstimateRecord(record);
};

export const calcTaxTotals = (estimate: EstimateRecord) => {
  let taxable_amount = 0;
  switch (estimate.default_taxable_amount) {
    case "product":
      taxable_amount = round(Math.max(0, estimate.product_total - estimate.discount), 2);
      break;
    case "labor":
      taxable_amount = round(Math.max(0, estimate.labor_total), 2);
      break;
    case "product_labor":
      taxable_amount = round(Math.max(0, estimate.product_total - estimate.discount + estimate.labor_total), 2);
      break;
    default:
      taxable_amount = round(Math.max(0, estimate.taxable_amount), 2);
  }

  let tax_total = 0;
  if (taxable_amount && estimate.tax_rate) {
    tax_total = round(taxable_amount * (estimate.tax_rate / 100.0), 2);
  }
  const total = round(estimate.product_total - estimate.discount + estimate.labor_total + tax_total, 2);

  return { taxable_amount, tax_total, total };
};

/**
 * Result of summing group totals - immutable
 */
interface GroupTotals {
  readonly subtotal: number;
  readonly labor_total: number;
  readonly product_total: number;
  readonly discountable_total: number;
}

export const calcEstimateSubtotals = (
  groupTotals: GroupTotals,
): {
  readonly subtotal: number;
  readonly labor_total: number;
  readonly product_total: number;
  readonly discountable_total: number;
} => ({
  subtotal: groupTotals.subtotal,
  labor_total: groupTotals.labor_total,
  product_total: groupTotals.product_total,
  discountable_total: groupTotals.discountable_total,
});

export const getTimezoneString = (timezoneOffset: number): string => {
  const sign = timezoneOffset > 0 ? "-" : "+";
  const totalMinutes = Math.abs(timezoneOffset);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${sign}${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
};

export const markupMultiplier = (markup: Nullable<number>): number => {
  return 1 + (markup || 0) / 100;
};

export type DefaultTaxableAmountType = "none" | "product" | "labor" | "product_labor";

export interface IEstimateData {
  id: number;
  kind: EstimateKind;
  activated_price_list_id: number;
  price_list_id: number;
  customer_name: string;
  name: string;
  calc_method: "standard";
  customer_phone: string;
  org_uid: string;
  uid: string;
  payment_terms: IEstimatePaymentTermsData;
  estimated_start: string;
  estimated_end: string;
  groups: IEstimateGroupData[];
  discounts: IEstimateDiscountData[];
  job_id: number;
  discount: number;
  subtotal: number;
  total: number;
  labor_total: number;
  product_total: number;
  discountable_total: number;
  created_at: DateStringNumber;
  updated_at: DateStringNumber;
  tax_rate: number;
  tax_total: number;
  taxable_amount: number;
  default_taxable_amount: DefaultTaxableAmountType;
  defaults: IDefaultsData;
  loading?: boolean;
  finance_options: IEstimateFinanceOptionData[];
  product_markup: Nullable<number>;
  labor_markup: Nullable<number>;
  metadata?: { [key: string]: unknown };
}

export interface IEstimateRecord {
  id: number;
  kind: EstimateKind;
  activated_price_list_id: number;
  price_list_id: Nullable<number>;
  customer_name: string;
  name: string;
  calc_method: "standard";
  customer_phone: string;
  org_uid: string;
  uid: string;
  payment_terms: EstimatePaymentTermsRecord;
  estimated_start: Nullable<Date>;
  estimated_end: Nullable<Date>;
  group_ids: List<number>;
  discounts: List<EstimateDiscountRecord>;
  job_id: number;
  discount: number;
  subtotal: number;
  total: number;
  labor_total: number;
  product_total: number;
  discountable_total: number;
  running_discountable: number;
  created_at: Date;
  updated_at: Date;
  tax_rate: number;
  tax_total: number;
  taxable_amount: number;
  default_taxable_amount: DefaultTaxableAmountType;
  defaults: DefaultsRecord;
  loading: boolean;
  finance_option_ids: List<number>;
  product_markup: Nullable<number>;
  labor_markup: Nullable<number>;
  metadata: Map<string, unknown>;
}

const defaultEstimateProps: IEstimateRecord = {
  id: 0,
  kind: "default",
  activated_price_list_id: 0,
  price_list_id: null,
  customer_name: "",
  name: "",
  calc_method: "standard",
  customer_phone: "",
  org_uid: "",
  uid: "",
  payment_terms: estimatePaymentTermsFromJSON({}),
  estimated_start: null,
  estimated_end: null,
  group_ids: List<number>(),
  discounts: List<EstimateDiscountRecord>(),
  job_id: 0,
  discount: 0,
  subtotal: 0,
  total: 0,
  labor_total: 0,
  product_total: 0,
  discountable_total: 0,
  running_discountable: 0,
  created_at: new Date(),
  updated_at: new Date(),
  tax_rate: 0,
  tax_total: 0,
  taxable_amount: 0,
  default_taxable_amount: "none",
  defaults: defaultsFromJSON({}),
  loading: false,
  finance_option_ids: List<number>(),
  product_markup: 0,
  labor_markup: 0,
  metadata: Map<string, unknown>(),
};

export class EstimateRecord extends Record(defaultEstimateProps) implements IEstimateRecord {}
